import React, { useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import Combobox from "../Combobox.jsx";
import { CURRENCIES } from "../../lib/constants";
import { fmtMoney, inputCls } from "../../lib/salary";
import { Modal, F } from "./PaymentDialog.jsx";

export default function RateHistoryDialog({ employee, onClose, onChanged }) {
  const [rates, setRates] = useState(employee.rates);
  const [form, setForm] = useState({
    monthly_salary: "",
    currency: employee.current_rate?.currency || "USD",
    effective_from: new Date().toISOString().slice(0, 7) + "-01",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const refresh = async () => {
    const { data } = await api.get("/salary/employees");
    setRates(data.find((e) => e.id === employee.id)?.rates || []);
    onChanged();
  };

  const add = async () => {
    if (!form.monthly_salary || Number(form.monthly_salary) <= 0) return toast.error("Monthly salary must be greater than 0");
    if (!form.effective_from) return toast.error("Effective From is required");
    setSaving(true);
    try {
      await api.post(`/salary/employees/${employee.id}/rates`, { ...form, monthly_salary: Number(form.monthly_salary) });
      toast.success("New salary added — previous periods are preserved");
      set("monthly_salary", "");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete salary record ${r.currency} ${fmtMoney(r.monthly_salary)} from ${r.effective_from}?`)) return;
    try {
      await api.delete(`/salary/rates/${r.id}`);
      toast.success("Salary record deleted");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Modal title={`Salary history — ${employee.name}`} onClose={onClose} testid="rate-history-dialog">
      <div className="border border-slate-200 rounded-xl overflow-hidden mb-5">
        <table className="w-full" data-testid="rate-history-table">
          <thead>
            <tr className="text-left text-xs font-semibold text-slate-500 uppercase bg-slate-50/70">
              <th className="px-3 py-2">Effective From</th>
              <th className="px-3 py-2 text-right">Monthly Salary</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rates.map((r, i) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`rate-row-${r.id}`}>
                <td className="px-3 py-2 text-sm tabular text-slate-700">{r.effective_from}{i === 0 && <span className="ml-2 text-[10px] uppercase font-semibold text-emerald-700">latest</span>}</td>
                <td className="px-3 py-2 text-sm text-right tabular font-semibold text-slate-900">{r.currency} {fmtMoney(r.monthly_salary)}</td>
                <td className="px-3 py-2 text-right">
                  {rates.length > 1 && <button type="button" onClick={() => remove(r)} className="text-slate-400 hover:text-red-600" data-testid={`rate-delete-${r.id}`}><Trash2 size={14} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="section-label mb-2">Add new salary (change from a given month)</div>
      <div className="grid grid-cols-3 gap-3">
        <F label="Monthly Salary" required>
          <input type="number" step="0.01" min="0" value={form.monthly_salary} onChange={(e) => set("monthly_salary", e.target.value)} className={`${inputCls} text-right tabular`} data-testid="rate-new-salary" />
        </F>
        <F label="Currency" required>
          <Combobox value={form.currency} onChange={(v) => set("currency", v)} options={CURRENCIES.map((c) => ({ value: c, label: c }))} testid="rate-new-currency" allowClear={false} />
        </F>
        <F label="Effective From" required>
          <input type="date" value={form.effective_from} onChange={(e) => set("effective_from", e.target.value)} className={inputCls} data-testid="rate-new-effective" />
        </F>
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button type="button" onClick={onClose} className="h-11 px-4 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="rate-history-done">Done</button>
        <button type="button" onClick={add} disabled={saving} className="h-11 px-5 rounded-lg bg-[#0055FF] hover:bg-[#0044CC] text-white text-sm font-semibold disabled:opacity-60" data-testid="rate-new-save">
          {saving ? "Saving…" : "Add salary change"}
        </button>
      </div>
    </Modal>
  );
}
